import { useEffect } from 'react';
import { useStoreValue } from '@/lib/tinyStore';
import { voiceSessionStore, type VoiceSessionState } from '@/features/voice/store/voiceSessionStore';
import { mediaBindingStore, type DesktopMediaBinding } from '../activeMedia';
import { withDesktop } from '../bridge';

const selectVoice = (
  state: VoiceSessionState,
): { connected: boolean; micEnabled: boolean; transmitting: boolean } => ({
  connected: state.status === 'connected',
  micEnabled: state.micEnabled,
  transmitting: state.transmitting,
});

const selectBinding = (binding: DesktopMediaBinding | null): boolean => binding?.micOn ?? false;

function shallow(
  a: { connected: boolean; micEnabled: boolean; transmitting: boolean },
  b: { connected: boolean; micEnabled: boolean; transmitting: boolean },
): boolean {
  return (
    a.connected === b.connected &&
    a.micEnabled === b.micEnabled &&
    a.transmitting === b.transmitting
  );
}

/**
 * WHAT THIS DOCUMENT'S MICROPHONE IS DOING — reported by EVERY window.
 *
 * It used to live inside `useDesktopPresence`, which the call window does not
 * mount (presence, the updater and the ringer all belong to the workspace), so
 * the shell had no idea a call window held any media at all. Two things
 * depended on knowing and both were wrong because of it:
 *
 *   • the TRAY said "In a call" only when the WORKSPACE was in one, so a call
 *     in its own window left the tray's Mute item greyed out;
 *   • the global mute hotkey had nothing to route by, so it was broadcast to
 *     both documents at once — one press, two gates, and a mute state that
 *     drifted permanently out of phase.
 *
 * With this mounted in both, `main/mediaRouter.ts` keeps one report per window,
 * shows the tray the aggregate, and sends each media hotkey to exactly one
 * document: the one that is actually holding a live call.
 *
 * A no-op in a browser — `withDesktop` has no bridge to run against.
 */
export function useDesktopMediaState(): void {
  const voice = useStoreValue(voiceSessionStore, selectVoice, shallow);
  const binding = useStoreValue(mediaBindingStore, selectBinding);
  const hasBinding = useStoreValue(mediaBindingStore, (value) => value !== null);

  useEffect(() => {
    const inCall = voice.connected || hasBinding;
    withDesktop((bridge) =>
      bridge.setMediaState({
        inCall,
        /* THE SAME ORDER THE HOTKEY USES — a binding first, then the voice
           channel. It was voice-first here and voice-first there, which agreed
           with itself and was wrong in the same direction twice: at `/meet/:id`
           the tray described a voice channel the user could not see while the
           room on screen was the meeting. See ../activeMedia.ts. */
        micOn: hasBinding ? binding : voice.micEnabled,
        /* FOR A CALL OR A MEETING THIS IS `micOn`, NOT "transmitting", and it
           says so here rather than in a bug report. `DesktopMediaBinding`
           exposes `micOn` and one setter and nothing that answers "is the
           published track live right now"; a room would have to publish that
           itself. Nothing in the shell reads this field — `mediaOwners`
           carries it into the tray aggregate and the tray shows only `inCall`
           and `micOn` — so it is inert rather than wrong. It stopped being
           inert once, when a reconciliation tried to compare it with the
           push-to-talk gate, and that could only tell a real gate from a
           refused one with a renderer acknowledgement, which was the fifth hot
           microphone. Anything that wants to READ this must fix it first. */
        transmitting: hasBinding ? binding : voice.transmitting,
      }),
    );
  }, [voice, binding, hasBinding]);
}
